"use client";

import React from "react";
import { Mail, Phone, MapPin, Clock, Circle, Loader, ArrowRight } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const contactDetails = [{
  icon: Phone,
  label: "Dispatch",
  value: "(913) 636-8169",
  description: "24/7 Emergency Support"
}, {
  icon: Mail,
  label: "Estimating",
  value: "field-ops@voltex-it.com",
  description: "Fast RFQ Turnaround"
}, {
  icon: MapPin,
  label: "HQ",
  value: "Johnson County KS",
  description: "Serving the Kansas City Metro Area"
}];
function StrategyForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const inputBaseClasses = "flex w-full min-w-0 rounded-sm border border-input bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none placeholder:text-muted-foreground disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm focus-visible:border-primary focus-visible:ring-primary/20 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-card/50";
  if (isSubmitted && !submitError) {
    return <div data-section-id="234476" className="flex flex-col items-center justify-center py-12 text-center space-y-6 bg-card/30 border border-border rounded-sm p-8">
        <div className="size-16 rounded-lg bg-primary/20 flex items-center justify-center mb-4">
          <Circle className="size-8 text-primary fill-primary" />
        </div>
        <h3 className="text-2xl font-default font-bold text-foreground">Request Logged</h3>
        <p className="text-muted-foreground max-w-md">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4 border-primary text-primary hover:bg-primary hover:text-primary-foreground">
          Submit New Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground uppercase tracking-widest text-xs font-bold font-mono">Contact Name</FormLabel>
              <FormControl>
                <Input placeholder="Project Manager Name" {...field} className="h-12 bg-card/50 border-border/50 focus-visible:border-primary rounded-sm" />
              </FormControl>
              <FormDescription className="text-xs font-mono">Primary site contact</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground uppercase tracking-widest text-xs font-bold font-mono">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="pm@contractor.com" {...field} className="h-12 bg-card/50 border-border/50 focus-visible:border-primary rounded-sm" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="phone" rules={{
        required: "Phone number is required",
        pattern: {
          value: /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/,
          message: "Invalid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground uppercase tracking-widest text-xs font-bold font-mono">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(816) 000-0000" {...field} className="h-12 bg-card/50 border-border/50 focus-visible:border-primary rounded-sm" />
              </FormControl>
              <FormDescription className="text-xs font-mono">For field coordination</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="preferred_date" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground uppercase tracking-widest text-xs font-bold font-mono">Requested Survey Date</FormLabel>
              <FormControl>
                <Input type="date" {...field} className="h-12 bg-card/50 border-border/50 focus-visible:border-primary rounded-sm [color-scheme:dark]" />
              </FormControl>
              <FormDescription className="text-xs font-mono">Target date for site visit</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="service_interest" rules={{
      required: "Please select a service type"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-muted-foreground uppercase tracking-widest text-xs font-bold font-mono">Service Category</FormLabel>
            <FormControl>
              <select {...field} className={cn(inputBaseClasses, "h-12 bg-card/50 border-border/50 focus-visible:border-primary appearance-none rounded-sm")}>
                <option value="" disabled>Select service type...</option>
                <option value="Structured Cabling">Structured Cabling</option>
                <option value="Fiber Optics">Fiber Optics</option>
                <option value="Wireless / Wi-Fi">Wireless / Wi-Fi</option>
                <option value="Security & Surveillance">Security & Surveillance</option>
                <option value="Audio / Visual">Audio / Visual</option>
                <option value="IT Field Support">IT Field Support</option>
              </select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="goals" rules={{
      required: "Please describe the project scope"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-muted-foreground uppercase tracking-widest text-xs font-bold font-mono">Project Scope & Details</FormLabel>
            <FormControl>
              <textarea {...field} placeholder="Provide site details, drop counts, or specific hardware requirements..." className={cn(inputBaseClasses, "min-h-[120px] py-3 bg-card/50 border-border/50 focus-visible:border-primary resize-y rounded-sm")} />
            </FormControl>
            <FormDescription className="text-xs font-mono">Include any specific GC or MSP requirements</FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-sm">
          <p className="text-destructive text-sm font-bold font-mono">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} size="lg" className="w-full sm:w-auto group bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-default font-bold uppercase tracking-wider">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Transmitting...
          </> : <>
            Request Site Survey
            <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
          </>}
      </Button>
    </Form>;
}
export default function LetsTalkStrategy() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: formRef,
    isVisible: isFormVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: contactRef,
    isVisible: isContactVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="request-service" className="bg-background text-foreground py-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/4 w-1/2 h-1/2 bg-primary/10 rounded-lg blur-[120px] pointer-events-none" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div ref={headerRef} className={cn("mb-16 max-w-2xl transition-all duration-700 ease-out", isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <h2 className="text-4xl md:text-6xl font-default font-bold tracking-tighter mb-4 uppercase">
            Partner with <span className="text-primary">Voltex</span>
          </h2>
          <p className="text-lg text-muted-foreground font-default">Partner with VOLTEX for reliable low-voltage execution in Kansas City. From structured cabling to complex IT field support, we provide the precision GCs and MSPs demand.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div ref={formRef} className={cn("lg:col-span-7 transition-all duration-700 delay-100 ease-out", isFormVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8")}>
            <FormProvider formKey="request_service_form_contact" formId="request_service_form_contact" sectionName="request-service" defaultValues={{
            name: "",
            email: "",
            phone: "",
            service_interest: "",
            goals: "",
            preferred_date: ""
          }} submitText="Request Site Survey" successMessage="Thank you for reaching out. A Voltex project lead will review your requirements and contact you shortly to discuss the next steps.">
              <StrategyForm />
            </FormProvider>
          </div>

          {}
          <div ref={contactRef} className={cn("lg:col-span-5 flex flex-col justify-between transition-all duration-700 delay-200 ease-out", isContactVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8")}>
            <div className="bg-card border border-border/50 rounded-sm p-8 backdrop-blur-sm h-full flex flex-col shadow-4xl">
              <div className="flex items-center justify-between mb-10 pb-6 border-b border-border/50">
                <h3 className="text-xl font-default font-bold uppercase tracking-wider">Field Operations</h3>
                <Badge variant="outline" className="bg-primary/10 border-primary/30 text-primary gap-2 py-1 px-3 rounded-sm font-mono text-[10px]">
                  <span className="relative flex size-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-lg bg-primary opacity-75"></span>
                    <span className="relative inline-flex rounded-lg size-2 bg-primary"></span>
                  </span>
                  SYSTEMS ACTIVE
                </Badge>
              </div>

              <div className="space-y-8 flex-grow">
                {contactDetails.map((detail, i) => <div key={i} data-index={i} className="flex items-start gap-5 group">
                    <div className="p-3 rounded-sm bg-background border border-border/50 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-300">
                      <detail.icon className="size-6" />
                    </div>
                    <div>
                      <p className="text-xs font-bold font-mono text-muted-foreground mb-1 uppercase tracking-widest">{detail.label}</p>
                      <p className="text-lg font-default font-bold text-foreground">{detail.value}</p>
                      <p className="text-sm text-muted-foreground mt-0.5 font-default">{detail.description}</p>
                    </div>
                  </div>)}
              </div>

              <div className="mt-12 pt-8 border-t border-border/50">
                <div className="flex items-center gap-4 text-muted-foreground">
                  <Clock className="size-5 text-primary" />
                  <div>
                    <p className="text-sm font-bold font-default text-foreground uppercase tracking-wider">Field Hours</p>
                    <p className="text-sm font-mono">07:00 - 18:00 CST | MON-FRI</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}